import React, { useState, useEffect } from 'react';
import {
    Card,
    List,
    Avatar,
    Typography,
    Badge,
    Input,
    Empty,
    Spin,
    Tag,
    Space,
    Button
} from 'antd';
import {
    MessageOutlined,
    UserOutlined,
    SearchOutlined,
    CalendarOutlined,
    PhoneOutlined
} from '@ant-design/icons';
import { useAuth } from '../../../contexts/AuthContext';
import api from '../../../services/api';
import dayjs from 'dayjs';

const { Text: AntText, Title } = Typography;
const { Search } = Input;

function ChatList({ onSelectLead, selectedLeadId }) {
    const { user } = useAuth();
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchText, setSearchText] = useState('');

    useEffect(() => {
        loadLeads();
    }, []);

    const loadLeads = async () => {
        try {
            setLoading(true);
            const response = user?.user_type === 'vendor' 
                ? await api.getVendorLeads({ per_page: 50 })
                : await api.getMyLeads({ per_page: 50 });
            
            if (response.success) {
                setLeads(response.data.data || []);
            }
        } catch (error) {
            console.error('Failed to load leads:', error);
        } finally {
            setLoading(false);
        }
    };

    const getOtherParticipant = (lead) => {
        if (user?.user_type === 'vendor') {
            return lead.user;
        } else {
            return lead.vendor;
        }
    };

    const getStatusColor = (status) => {
        const colors = {
            pending: 'orange',
            quoted: 'blue',
            accepted: 'green',
            rejected: 'red',
            completed: 'green'
        };
        return colors[status] || 'default';
    };

    const filteredLeads = leads.filter(lead => {
        const otherParticipant = getOtherParticipant(lead);
        const searchLower = searchText.toLowerCase();
        
        return (
            otherParticipant?.name?.toLowerCase().includes(searchLower) ||
            lead.event_type?.toLowerCase().includes(searchLower) ||
            lead.venue?.toLowerCase().includes(searchLower)
        );
    });

    const formatLastMessageTime = (timestamp) => {
        const now = dayjs();
        const messageTime = dayjs(timestamp);
        
        if (now.diff(messageTime, 'day') === 0) {
            return messageTime.format('HH:mm');
        } else if (now.diff(messageTime, 'day') === 1) {
            return 'Yesterday';
        } else {
            return messageTime.format('MMM DD');
        }
    };

    // Get conversation category/type
    const getConversationCategory = (lead) => {
        if (lead.order_id || lead.status === 'quoted' || lead.status === 'accepted' || lead.status === 'confirmed') {
            return 'Order';
        } else if (lead.event_type && (lead.event_type.toLowerCase().includes('wedding') || lead.event_type.toLowerCase().includes('event'))) {
            return 'General';
        } else if (lead.event_type) {
            return 'General';
        }
        return 'Support';
    };

    // Get conversation title
    const getConversationTitle = (lead) => {
        if (lead.order_id) {
            const orderNum = lead.order_id.toString().padStart(5, '0');
            return `Order #${orderNum} - ${lead.event_type || 'Delivery Question'}`;
        } else if (lead.event_type) {
            // Format event type properly
            const eventType = lead.event_type.charAt(0).toUpperCase() + lead.event_type.slice(1).toLowerCase();
            if (eventType.toLowerCase().includes('wedding')) {
                return 'Catering for Wedding Event';
            }
            return `Catering for ${eventType} Event`;
        }
        return 'Menu Customization Request';
    };

    return (
        <div className="h-full flex flex-col bg-[#FAF8F5]">
            {/* Header */}
            <div className="p-4 sm:p-6 bg-white border-b border-[#EAE7E3]">
                <div className="flex items-center mb-4">
                    <MessageOutlined className="text-[#C97D60] text-xl mr-3" />
                    <Title level={2} className="!mb-0 !text-[#2C2C2C] font-bold" style={{ fontFamily: 'var(--font-serif)' }}>
                        Messages
                    </Title>
                </div>
                <Input
                    placeholder="Search conversations..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    prefix={<SearchOutlined className="text-[#4A4A4A]" />}
                    allowClear
                    className="rounded-lg border-[#EAE7E3] bg-[#FAF8F5] focus:border-[#C97D60] focus:ring-[#C97D60]"
                    style={{ 
                        borderRadius: '8px',
                        height: '44px'
                    }}
                />
            </div>

            {/* Leads List */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-3 pb-24">
                {loading ? (
                    <div className="flex justify-center items-center h-32">
                        <Spin size="large" />
                    </div>
                ) : filteredLeads.length === 0 ? (
                    <Empty 
                        description="No conversations yet"
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                ) : (
                    filteredLeads.map((lead) => {
                        const isSelected = selectedLeadId === lead.id;
                        const category = getConversationCategory(lead);
                        const title = getConversationTitle(lead);
                        
                        return (
                            <div
                                key={lead.id}
                                className={`cursor-pointer transition-all duration-200 p-4 bg-white rounded-lg border border-[#EAE7E3] hover:border-[#C97D60]/30 hover:shadow-md ${
                                    isSelected ? 'border-[#C97D60] shadow-md' : ''
                                }`}
                                onClick={() => onSelectLead(lead)}
                            >
                                <div className="mb-2">
                                    <Title level={5} className="!mb-1 !text-[#2C2C2C] !font-bold !text-base">
                                        {title}
                                    </Title>
                                    <AntText type="secondary" className="text-xs text-[#4A4A4A]">
                                        {category}
                                    </AntText>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}

export default ChatList;
